import {
  cardStyles,
  Divider,
  SectionWithHeader,
  SpecList,
  Typography,
} from '@maia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { getQuotationOptions } from './../../../../features/quotation/quotation/queryOptions';
import { getProductsOptions } from './../../../../features/quotation/products/queryOptions';
import { twMerge } from 'tailwind-merge';
import { match, P } from 'ts-pattern';
import { formatAddress, formatDate } from '@maia/utils';
import { AddressDto } from '@maia/shared';
import { Fragment } from 'react';

export const Route = createFileRoute(
  '/_authenticated/quotations/$guid/_layout/premium-data',
)({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { guid } }) => {
    const quotation = await queryClient.ensureQueryData(
      getQuotationOptions(guid),
    );

    const products = await queryClient.ensureQueryData(
      getProductsOptions(guid),
    );

    return { products, quotation };
  },
});

function RouteComponent() {
  const data = Route.useLoaderData();

  return (
    <>
      {data.products.map((product, index, all) => {
        const activeAddons = product.addOns?.filter(
          (addon) => addon.answer.value === 'J',
        );

        return (
          <Fragment key={product.guid}>
            <Typography
              variant="body-l"
              weight="medium"
              className="col-span-full"
            >
              {product.name}
            </Typography>

            <SectionWithHeader
              title="Gegevens van de verzekering"
              className="col-span-full"
            >
              <div
                className={twMerge(cardStyles({ variant: 'outlined' }), 'p-20')}
              >
                <SpecList
                  items={product.questions.map((question) => {
                    const activeOption = question.options.find(
                      (option) => option.value === question.answer.value,
                    );

                    return {
                      title: question.phrasing,
                      value: match(activeOption?.label ?? question.answer.value)
                        .with(
                          P.when(
                            (a): a is AddressDto => a instanceof AddressDto,
                          ),
                          (address) => formatAddress(address),
                        )
                        .with(
                          P.when((a): a is Date => a instanceof Date),
                          (date) => formatDate(date),
                        )
                        .otherwise((answer) => answer),
                    };
                  })}
                />
              </div>
            </SectionWithHeader>

            {product.object && (
              <SectionWithHeader
                title="Gegevens van het object"
                className="col-span-full"
              >
                <div
                  className={twMerge(
                    cardStyles({ variant: 'outlined' }),
                    'p-20',
                  )}
                >
                  <SpecList
                    items={product.object.specs
                      .filter((spec) => !!spec.answer.value)
                      .map((spec) => {
                        return {
                          title: spec.description,
                          value: match(spec.answer.value)
                            .with(
                              P.when(
                                (a): a is AddressDto => a instanceof AddressDto,
                              ),
                              (address) => formatAddress(address),
                            )
                            .with(
                              P.when((a): a is Date => a instanceof Date),
                              (date) => formatDate(date),
                            )

                            .otherwise((answer) => answer),
                        };
                      })}
                  />
                </div>
              </SectionWithHeader>
            )}

            {activeAddons && activeAddons?.length > 0 && (
              <Typography
                variant="body-m"
                weight="medium"
                className="col-span-full"
              >
                Dekkingen
              </Typography>
            )}

            {activeAddons &&
              activeAddons?.length > 0 &&
              activeAddons.map((addon) => (
                <SectionWithHeader
                  title={addon.phrasing}
                  className="col-span-full"
                  key={`${product.guid}-${addon.guid}`}
                >
                  <div
                    className={twMerge(
                      cardStyles({ variant: 'outlined' }),
                      'p-20',
                    )}
                  >
                    <SpecList
                      items={addon.children?.flatMap((child) => {
                        if (!child.answer.value) {
                          return [];
                        }

                        return {
                          title: child.phrasing,
                          value: match(child.answer.value)
                            .with(
                              P.when(
                                (a): a is AddressDto => a instanceof AddressDto,
                              ),
                              (address) => formatAddress(address),
                            )
                            .with(
                              P.when((a): a is Date => a instanceof Date),
                              (date) => formatDate(date),
                            )
                            .otherwise((answer) => answer),
                        };
                      })}
                    />
                  </div>
                </SectionWithHeader>
              ))}

            {index < all.length - 1 && <Divider className="col-span-full" />}
          </Fragment>
        );
      })}
    </>
  );
}
